import { r as registerInstance, c as createEvent, h } from './index-662ec80e.js';
import { p as propCheck, r as runWatchers, h as hasPropError, d as displayPropError } from './propCheck-e7571e3e.js';
import { t as vDate, u as vDateMin, w as vDateMax, b as vMinLength, a as vString, a1 as vUndefOr, Q as vBoolean, $ as vNullOr } from './validation-02b8d01d.js';
import { c as currentLocale, i as dateFormatNumeric } from './i18n-939e2dee.js';

const DsInputDate = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.dsInput = createEvent(this, "dsInput", 7);
    this.nextRenderPromises = [];
    this.propError = {};
    this.datePartIndex = undefined;
    this.imaskPattern = '';
    this.placeholder = '';
    this.currentLocale = currentLocale();
    this.name = undefined;
    this.label = '';
    this.helperText = undefined;
    this.isFloatLabel = false;
    this.isMini = false;
    this.isDisabled = false;
    this.isReadonly = false;
    this.isClearable = false;
    this.isWithoutStyling = false;
    this.isRequired = false;
    this.value = null;
    this.max = undefined;
    this.min = undefined;
    this.dsInputValue = '';
  }
  updateLocale() {
    this.currentLocale = currentLocale();
    const exampleDate = dateFormatNumeric(new Date('1324-12-28T00:00:00.000Z'), true);
    const dateParts = exampleDate.replace(/\D+/g, ' ').split(' ');
    this.datePartIndex = {
      day: dateParts.findIndex((d) => d === '28'),
      month: dateParts.findIndex((m) => m === '12'),
      year: dateParts.findIndex((y) => y === '1324')
    };
    this.imaskPattern = exampleDate
      .replace(/(\D)/g, '{$1}') // quote anything not a number
      .replace(/[0-9]/g, '0') // numbers to masked numbers
      .replace(/\b00\b/g, '`0[0]') // 2 digit day/month, optional digit, prevent shiftback
      .replace(/\b0000\b/, '`0000'); // 4 digit year, prevent shiftback
    this.placeholder = exampleDate
      .replace(/\b28\b/, 'dd')
      .replace(/\b12\b/, 'mm')
      .replace(/\b1324\b/, 'yyyy');
  }
  watchName() {
    /* istanbul ignore if */
    if (!propCheck(this, 'name', 'string', vMinLength(1)))
      return;
  }
  watchLabel() {
    /* istanbul ignore if */
    if (!propCheck(this, 'label', 'string', vString))
      return;
  }
  watchHelperText() {
    /* istanbul ignore if */
    if (!propCheck(this, 'helperText', 'string', vUndefOr(vString)))
      return;
  }
  watchIsFloatLabel() {
    /* istanbul ignore if */
    if (!propCheck(this, 'isFloatLabel', 'boolean', vBoolean))
      return;
  }
  watchIsMini() {
    /* istanbul ignore if */
    if (!propCheck(this, 'isMini', 'boolean', vBoolean))
      return;
  }
  async watchIsDisabled() {
    /* istanbul ignore if */
    if (!propCheck(this, 'isDisabled', 'boolean', vBoolean))
      return;
    if (this.isDisabled)
      this.dsInput.emit(null);
  }
  async watchIsReadonly() {
    /* istanbul ignore if */
    if (!propCheck(this, 'isReadonly', 'boolean', vBoolean))
      return;
  }
  async watchIsClearable() {
    /* istanbul ignore if */
    if (!propCheck(this, 'isClearable', 'boolean', vBoolean))
      return;
  }
  watchIsWithoutStyling() {
    /* istanbul ignore if */
    if (!propCheck(this, 'isWithoutStyling', 'boolean', vBoolean))
      return;
  }
  watchIsRequired() {
    /* istanbul ignore if */
    if (!propCheck(this, 'isRequired', 'boolean', vBoolean))
      return;
  }
  watchValue() {
    /* istanbul ignore if */
    if (!propCheck(this, 'value', 'DateString | null', vNullOr(vDate)))
      return;
    // if the incoming value is different from the displayed value, update the displayed value (developer used .value to update, not a dsInput event)
    if (this.value !== this.displayToIso(this.dsInputValue))
      this.dsInputValue = this.isoToDisplay(this.value);
  }
  watchMax() {
    /* istanbul ignore if */
    if (!propCheck(this, 'max', 'StringDate', vUndefOr(vDate)))
      return;
  }
  watchMin() {
    /* istanbul ignore if */
    if (!propCheck(this, 'min', 'StringDate', vUndefOr(vDate)))
      return;
  }
  async injectError(error) {
    return this.dsInputElement.injectError(error);
  }
  async hideError() {
    return this.dsInputElement.hideError();
  }
  validate(display) {
    let error;
    const value = this.displayToIso(display);
    if (this.isRequired || display)
      error = vDate(value);
    if (error)
      return error;
    if (!this.isRequired && !value)
      return;
    if (this.min)
      error = vDateMin(this.min)(value);
    if (error)
      return error;
    if (this.max)
      error = vDateMax(this.max)(value);
    if (error)
      return error;
  }
  isoToDisplay(iso) {
    if (!iso)
      return null;
    return dateFormatNumeric(new Date(iso + 'T00:00:00.000Z'), true);
  }
  displayToIso(display) {
    if (!display)
      return null;
    const parts = display.replace(/\D+/g, ' ').split(' ').map(s => parseInt(s, 10));
    const day = parts[this.datePartIndex.day];
    const month = parts[this.datePartIndex.month];
    const year = parts[this.datePartIndex.year];
    if (day === undefined || isNaN(day) || month === undefined || isNaN(month) || year === undefined || isNaN(year))
      return null;
    if (year < 1000 || year >= 10000)
      return null;
    const value = `${year}-${month < 10 ? '0' : ''}${month}-${day < 10 ? '0' : ''}${day}`;
    const parsedDate = new Date(value + 'T00:00:00.000Z');
    if (isNaN(parsedDate.getTime()) || parsedDate.toISOString().split('T')[0] !== value)
      return null;
    return value;
  }
  connectedCallback() {
    runWatchers(this);
    this.updateLocale();
  }
  render() {
    if (hasPropError(this))
      return displayPropError(this);
    return (h("ds-input-string", { ref: el => {
        this.dsInputElement = el;
        this.inputElement = el.querySelector('input[type=text]');
      }, name: this.name, label: this.label, placeholder: this.placeholder, type: "text", helperText: this.helperText, isFloatLabel: this.isFloatLabel, isClearable: this.isClearable, isMini: this.isMini, isDisabled: this.isDisabled, isReadonly: this.isReadonly, isWithoutStyling: this.isWithoutStyling, validate: (value) => this.validate(value), value: this.dsInputValue, onDsInput: e => {
        e.preventDefault();
        e.stopPropagation();
        this.dsInputValue = e.detail || '';
        const newValue = this.displayToIso(e.detail);
        if (newValue !== this.value)
          this.dsInput.emit(newValue);
      }, mask: this.imaskPattern }, h("div", { slot: "helperText" }, h("slot", { name: "helperText" }))));
  }
  static get watchers() { return {
    "name": ["watchName"],
    "label": ["watchLabel"],
    "helperText": ["watchHelperText"],
    "isFloatLabel": ["watchIsFloatLabel"],
    "isMini": ["watchIsMini"],
    "isDisabled": ["watchIsDisabled"],
    "isReadonly": ["watchIsReadonly"],
    "isClearable": ["watchIsClearable"],
    "isWithoutStyling": ["watchIsWithoutStyling"],
    "isRequired": ["watchIsRequired"],
    "value": ["watchValue"],
    "max": ["watchMax"],
    "min": ["watchMin"]
  }; }
};

export { DsInputDate as ds_input_date };
